/**
 * AuthScreen - Unified authentication interface
 *
 * Features:
 * - Google OAuth (primary method)
 * - Email/password sign-in (Relay is invite-only: no sign-up here)
 * - Form validation (email format, password length)
 * - Error messaging
 */

import { useState } from 'react';
import { signInWithGoogle, signInWithEmail } from '../lib/auth';
import { StarburstMark } from './StarburstMark';

interface AuthScreenProps {
  onSignInError?: (error: string) => void;
  /** An invite was refused for a non-Google sign-in: show only the message and Google sign-in. */
  inviteNotice?: string | null;
}

export function AuthScreen({ onSignInError, inviteNotice }: AuthScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleGoogleSignIn() {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Sign-in failed';
      setError(errorMsg);
      if (onSignInError) onSignInError(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailAuth(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await signInWithEmail(email, password);
    } catch (e) {
      const errorMsg = e instanceof Error ? e.message : 'Sign-in failed';
      setError(errorMsg);
      if (onSignInError) onSignInError(errorMsg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="panel auth-card">
        <div className="panel-inner">
          <div className="panel-top centered">
            <StarburstMark size={44} />
            <h1 className="auth-wordmark">relay</h1>
            <p className="auth-tagline">You decide. AI does the work in between.</p>
          </div>

          <div className="auth-body">
            {inviteNotice && (
              <div role="alert" className="callout error">
                {inviteNotice}
              </div>
            )}

            <button type="button" className="btn primary" onClick={handleGoogleSignIn} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in with Google'}
            </button>

            {/* Divider and password form are hidden while an invite needs Google */}
            {!inviteNotice && (
              <>
                <div className="auth-divider">or</div>

                <form className="auth-form" onSubmit={handleEmailAuth}>
                  <label htmlFor="email" className="auth-label" style={{ marginTop: 0 }}>Email</label>
                  <input
                    id="email"
                    type="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={loading}
                  />
                  <label htmlFor="password" className="auth-label">Password</label>
                  <input
                    id="password"
                    type="password"
                    className="input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                  />
                  <button type="submit" className="btn" disabled={loading || !email || !password}>
                    Sign in with email
                  </button>
                </form>

                <div className="callout info">
                  Relay is invite-only. To join a workspace, use the invite link a workspace Lead sent you.
                </div>
              </>
            )}

            {error && (
              <div role="alert" className="callout error">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
